import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const PromoCodesTab = ({
  categories,
  customers,
  deletePromoCode,
  editingPromoCode,
  handlePromoCodeSubmit,
  isPromoCodeDialogOpen,
  openEditPromoCodeDialog,
  promoCodeForm,
  promoCodes,
  resetPromoCodeForm,
  setIsPromoCodeDialogOpen,
  setPromoCodeForm,
}) => (
          <TabsContent value="promo-codes">
            <div className="space-y-6">
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5 text-[#FFC107]" />
                    Promo Codes ({promoCodes.length})
                  </CardTitle>
                  <Dialog open={isPromoCodeDialogOpen} onOpenChange={setIsPromoCodeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-[#FFC107] text-black hover:bg-[#F59E0B]"
                        onClick={() => resetPromoCodeForm()}
                        data-testid="add-promo-code-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Promo Code
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141417] border-[#27272A] max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-white">
                          {editingPromoCode ? 'Edit Promo Code' : 'Add Promo Code'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handlePromoCodeSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Code *</Label>
                          <Input
                            value={promoCodeForm.code}
                            onChange={(e) => setPromoCodeForm({...promoCodeForm, code: e.target.value.toUpperCase()})}
                            placeholder="e.g., TASTY10"
                            className="bg-[#0B0B0C] border-[#27272A] uppercase"
                            required
                            data-testid="promo-code-form-code"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Discount Type</Label>
                            <Select 
                              value={promoCodeForm.discount_type}
                              onValueChange={(value) => setPromoCodeForm({...promoCodeForm, discount_type: value})}
                            >
                              <SelectTrigger className="bg-[#0B0B0C] border-[#27272A]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#141417] border-[#27272A]">
                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                                <SelectItem value="fixed">Fixed (CHF)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Discount Value *</Label>
                            <Input
                              type="number"
                              value={promoCodeForm.discount_value}
                              onChange={(e) => setPromoCodeForm({...promoCodeForm, discount_value: e.target.value})}
                              placeholder={promoCodeForm.discount_type === 'percentage' ? '10' : '5'}
                              className="bg-[#0B0B0C] border-[#27272A]"
                              required
                              data-testid="promo-code-form-value"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Min Order (CHF)</Label>
                            <Input
                              type="number"
                              value={promoCodeForm.min_order_amount}
                              onChange={(e) => setPromoCodeForm({...promoCodeForm, min_order_amount: e.target.value})}
                              placeholder="0"
                              className="bg-[#0B0B0C] border-[#27272A]"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Max Uses</Label>
                            <Input
                              type="number"
                              value={promoCodeForm.max_uses}
                              onChange={(e) => setPromoCodeForm({...promoCodeForm, max_uses: e.target.value})}
                              placeholder="Unlimited"
                              className="bg-[#0B0B0C] border-[#27272A]"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Expires On</Label>
                          <Input
                            type="date"
                            value={promoCodeForm.expires_at}
                            onChange={(e) => setPromoCodeForm({...promoCodeForm, expires_at: e.target.value})}
                            className="bg-[#0B0B0C] border-[#27272A]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Applicable Categories</Label>
                          <div className="space-y-2 max-h-40 overflow-y-auto bg-[#0B0B0C] border border-[#27272A] rounded-md p-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={promoCodeForm.applicable_categories.length === 0}
                                onChange={() => setPromoCodeForm({...promoCodeForm, applicable_categories: []})}
                                className="accent-[#FFC107]"
                              />
                              <span className="text-sm text-white font-medium">All Categories</span>
                            </label>
                            <div className="border-t border-[#27272A] my-1"></div>
                            {categories.map(cat => (
                              <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={promoCodeForm.applicable_categories.includes(cat.id)}
                                  onChange={(e) => {
                                    const updated = e.target.checked
                                      ? [...promoCodeForm.applicable_categories, cat.id]
                                      : promoCodeForm.applicable_categories.filter(c => c !== cat.id);
                                    setPromoCodeForm({...promoCodeForm, applicable_categories: updated});
                                  }}
                                  className="accent-[#FFC107]"
                                />
                                <span className="text-sm text-zinc-300">{cat.name}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-zinc-500">
                            {promoCodeForm.applicable_categories.length === 0 
                              ? 'Discount applies to all items' 
                              : `Discount applies to ${promoCodeForm.applicable_categories.length} selected categor${promoCodeForm.applicable_categories.length === 1 ? 'y' : 'ies'}`}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label>Active</Label>
                          <Switch
                            checked={promoCodeForm.is_active}
                            onCheckedChange={(checked) => setPromoCodeForm({...promoCodeForm, is_active: checked})}
                          />
                        </div>
                        <Button type="submit" className="w-full bg-[#FFC107] text-black hover:bg-[#F59E0B]">
                          {editingPromoCode ? 'Update Promo Code' : 'Create Promo Code'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {promoCodes.length === 0 ? (
                    <div className="text-center py-12">
                      <Tag className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500">No promo codes yet</p>
                      <p className="text-zinc-600 text-sm">Create promo codes for customers to use at checkout</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#27272A]">
                          <TableHead className="text-zinc-400">Code</TableHead>
                          <TableHead className="text-zinc-400">Discount</TableHead>
                          <TableHead className="text-zinc-400">Categories</TableHead>
                          <TableHead className="text-zinc-400">Min Order</TableHead>
                          <TableHead className="text-zinc-400">Usage</TableHead>
                          <TableHead className="text-zinc-400">Expires</TableHead>
                          <TableHead className="text-zinc-400">Status</TableHead>
                          <TableHead className="text-zinc-400">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {promoCodes.map((code) => (
                          <TableRow key={code.id} className="border-[#27272A]">
                            <TableCell className="font-mono font-bold text-[#FFC107]">
                              {code.code}
                            </TableCell>
                            <TableCell className="text-white">
                              {code.discount_type === 'percentage' 
                                ? `${code.discount_value}%` 
                                : `CHF ${code.discount_value}`}
                            </TableCell>
                            <TableCell className="text-zinc-300 text-sm max-w-[150px]">
                              {!code.applicable_categories || code.applicable_categories.length === 0 
                                ? <span className="text-zinc-500">All</span>
                                : code.applicable_categories.map(catId => {
                                    const cat = categories.find(c => c.id === catId);
                                    return cat ? cat.name : catId;
                                  }).join(', ')
                              }
                            </TableCell>
                            <TableCell className="text-zinc-300">
                              {code.min_order_amount > 0 ? `CHF ${code.min_order_amount}` : '-'}
                            </TableCell>
                            <TableCell className="text-zinc-300">
                              {code.times_used || 0}{code.max_uses ? `/${code.max_uses}` : ''}
                            </TableCell>
                            <TableCell className="text-zinc-400 text-sm">
                              {code.expires_at 
                                ? new Date(code.expires_at).toLocaleDateString() 
                                : 'Never'}
                            </TableCell>
                            <TableCell>
                              {code.is_active ? (
                                <Badge className="bg-green-500/20 text-green-500">Active</Badge>
                              ) : (
                                <Badge className="bg-zinc-500/20 text-zinc-500">Inactive</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => openEditPromoCodeDialog(code)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-400 hover:text-red-300"
                                  onClick={() => deletePromoCode(code.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
);

export default PromoCodesTab;
